import { useState } from 'react';
import moment from 'moment-timezone';

const useRecurrence = (searchCriteria, initialRecurrenceData, initialOccurrenceDates, workingDays = []) => {
  const [recurrenceData, setRecurrenceData] = useState(initialRecurrenceData || null);
  const [occurrenceDates, setOccurrenceDates] = useState(initialOccurrenceDates || [searchCriteria?.date]);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);

  const isWorkingDay = (momentDate) => {
    if (!workingDays || workingDays.length === 0) return true;
    const dayName = momentDate.format('dddd').toUpperCase();
    return workingDays.includes(dayName);
  };

  const isWeekend = (momentDate) => {
    const day = momentDate.day();
    return day === 0 || day === 6;
  };

  const generateOccurrenceDates = (recurrenceData) => {
    if (!recurrenceData) return [searchCriteria?.date];

    const occurrences = [];
    const startDate = moment(searchCriteria?.date);
    let currentDate = startDate.clone();
    const type = recurrenceData.recurrenceType?.toLowerCase();
    const pattern = recurrenceData.pattern;
    const interval = recurrenceData.interval || 1;

    const isWeekdayPattern = type === 'daily' && pattern === 'weekday';

    const isAllowedDay = (d) => {
      if (isWeekdayPattern) {
        return !isWeekend(d);
      }
      return isWorkingDay(d);
    };

    while (!isAllowedDay(currentDate)) {
      currentDate.add(1, 'day');
    }

    const addAllowedDays = (date, count) => {
      let remaining = count;
      while (remaining > 0) {
        date.add(1, 'day');
        if (isAllowedDay(date)) {
          remaining--;
        }
      }
      return date;
    };

    if (recurrenceData.ends === 'after') {
      const maxOccurrences = recurrenceData.occurrences || 1;
      let count = 0;
      let safetyCounter = 0;

      while (count < maxOccurrences && safetyCounter < 1000) {
        safetyCounter++;

        if (isAllowedDay(currentDate)) {
          occurrences.push(currentDate.format('YYYY-MM-DD'));
          count++;

          if (count >= maxOccurrences) break;
        }

        switch (type) {
          case 'daily':
            if (isWeekdayPattern) {
              do {
                currentDate.add(1, 'day');
              } while (isWeekend(currentDate));
            } else {
              addAllowedDays(currentDate, interval);
            }
            break;
          case 'weekly':
            currentDate.add(interval, 'weeks');
            while (!isAllowedDay(currentDate)) {
              currentDate.add(1, 'day');
            }
            break;
          case 'monthly':
            currentDate.add(interval, 'months');
            while (!isAllowedDay(currentDate)) {
              currentDate.add(1, 'day');
            }
            break;
          default:
            count = maxOccurrences;
        }
      }
    } else {
      const endDate = moment(recurrenceData.endDate);
      let safetyCounter = 0;

      while (!currentDate.isAfter(endDate) && safetyCounter < 1000) {
        safetyCounter++;

        if (isAllowedDay(currentDate)) {
          occurrences.push(currentDate.format('YYYY-MM-DD'));
        }

        switch (type) {
          case 'daily':
            if (isWeekdayPattern) {
              do {
                currentDate.add(1, 'day');
              } while (isWeekend(currentDate));
            } else {
              addAllowedDays(currentDate, interval);
            }
            break;
          case 'weekly':
            currentDate.add(interval, 'weeks');
            while (!isAllowedDay(currentDate) && !currentDate.isAfter(endDate)) {
              currentDate.add(1, 'day');
            }
            break;
          case 'monthly':
            currentDate.add(interval, 'months');
            while (!isAllowedDay(currentDate) && !currentDate.isAfter(endDate)) {
              currentDate.add(1, 'day');
            }
            break;
          default:
            currentDate = endDate.clone().add(1, 'day');
        }
      }
    }

    return occurrences.length > 0 ? occurrences : [searchCriteria?.date];
  };

  const getRecurrenceDisplayText = () => {
    if (!recurrenceData) return null;

    const typeMap = {
      'daily': 'Daily',
      'weekly': 'Weekly',
      'monthly': 'Monthly'
    };

    const typeText = typeMap[recurrenceData.recurrenceType?.toLowerCase()] || 'Recurring';
    const datesToUse = occurrenceDates.length > 0 ? occurrenceDates : generateOccurrenceDates(recurrenceData);

    if (recurrenceData.ends === 'after') {
      return `${typeText} (${datesToUse.length} ${datesToUse.length === 1 ? 'occurrence' : 'occurrences'})`;
    } else {
      return `${typeText} (${datesToUse.length} ${datesToUse.length === 1 ? 'occurrence' : 'occurrences'} until ${moment(recurrenceData.endDate).format('MMM DD, YYYY')})`;
    }
  };

  const getTotalOccurrences = () => {
    return occurrenceDates.length > 0 ? occurrenceDates.length : generateOccurrenceDates(recurrenceData).length;
  };

  return {
    recurrenceData,
    setRecurrenceData,
    occurrenceDates,
    setOccurrenceDates,
    showRecurrenceModal,
    setShowRecurrenceModal,
    generateOccurrenceDates,
    getRecurrenceDisplayText,
    getTotalOccurrences
  };
};

export default useRecurrence;